// Shared attachment picker for outgoing email (ENG-113). Compose and Reply both send files
// inline through Graph's single-shot actions (sendMail / reply / replyAll), so both are bound
// by the same ~4 MB request ceiling. One picker means the two dialogs can never disagree about
// the limit; the server enforces the same caps (server/lib/emailCompose.ts).

import { useCallback, useState } from "react";
import { Paperclip, X } from "lucide-react";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";

/** Raw bytes; ≈3.3 MB base64, under Graph's ~4 MB single-request ceiling. */
export const ATTACHMENT_CAP_BYTES = 2.5 * 1024 * 1024;

export interface EmailAttachmentDraft {
  name: string;
  contentType: string;
  contentBytes: string; // base64, no data: prefix
  size: number; // raw bytes, for the running total
}

export type EmailAttachmentPayload = { name: string; contentType: string; contentBytes: string };

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // data:<mime>;base64,<payload> — strip the prefix, Graph wants just the payload.
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
}

const mb = (bytes: number) => (bytes / 1024 / 1024).toFixed(1);

export interface EmailAttachmentsState {
  attachments: EmailAttachmentDraft[];
  totalBytes: number;
  overCap: boolean;
  addFiles: (files: FileList | null) => Promise<void>;
  remove: (index: number) => void;
  clear: () => void;
  /** The wire shape for the send call, or undefined when nothing is attached. */
  payload: () => EmailAttachmentPayload[] | undefined;
}

export function useEmailAttachments(): EmailAttachmentsState {
  const { toast } = useToast();
  const [attachments, setAttachments] = useState<EmailAttachmentDraft[]>([]);

  const totalBytes = attachments.reduce((sum, a) => sum + a.size, 0);
  const overCap = totalBytes > ATTACHMENT_CAP_BYTES;

  const addFiles = useCallback(
    async (files: FileList | null) => {
      if (!files?.length) return;
      const additions: EmailAttachmentDraft[] = [];
      for (const file of Array.from(files)) {
        additions.push({
          name: file.name,
          contentType: file.type || "application/octet-stream",
          contentBytes: await readFileAsBase64(file),
          size: file.size,
        });
      }
      // Warn from the event, never from inside a state updater: React may defer an updater to
      // the next render (ENG-114), and StrictMode runs updaters twice.
      const addedBytes = additions.reduce((sum, a) => sum + a.size, 0);
      const projected = totalBytes + addedBytes;
      if (projected > ATTACHMENT_CAP_BYTES) {
        toast({
          title: "Attachments too large",
          description: `Total attached size must stay under 2.5 MB (currently ${mb(projected)} MB).`,
          variant: "destructive",
        });
      }
      setAttachments((prev) => [...prev, ...additions]);
    },
    [toast, totalBytes],
  );

  const remove = useCallback((index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const clear = useCallback(() => setAttachments([]), []);

  const payload = useCallback(
    () =>
      attachments.length
        ? attachments.map(({ name, contentType, contentBytes }) => ({ name, contentType, contentBytes }))
        : undefined,
    [attachments],
  );

  return { attachments, totalBytes, overCap, addFiles, remove, clear, payload };
}

export function EmailAttachmentPicker({ state, disabled }: { state: EmailAttachmentsState; disabled?: boolean }) {
  const { attachments, totalBytes, overCap, addFiles, remove } = state;
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <Label className="text-xs">
          Attachments <span className="text-muted-foreground">(2.5 MB total)</span>
        </Label>
        <label
          className={`inline-flex items-center gap-1 text-xs text-violet-600 hover:text-violet-700 ${
            disabled ? "pointer-events-none opacity-50" : "cursor-pointer"
          }`}
        >
          <Paperclip className="h-3 w-3" />
          Attach
          <input
            type="file"
            multiple
            className="hidden"
            disabled={disabled}
            onChange={(e) => {
              void addFiles(e.target.files);
              e.target.value = "";
            }}
          />
        </label>
      </div>
      {attachments.length > 0 && (
        <ul className="space-y-1">
          {attachments.map((att, i) => (
            <li key={`${att.name}-${i}`} className="flex items-center justify-between text-xs bg-muted/40 rounded px-2 py-1">
              <span className="truncate">
                {att.name} <span className="text-muted-foreground">({(att.size / 1024).toFixed(0)} KB)</span>
              </span>
              <button
                type="button"
                onClick={() => remove(i)}
                disabled={disabled}
                className="text-muted-foreground hover:text-destructive"
                aria-label={`Remove ${att.name}`}
              >
                <X className="h-3 w-3" />
              </button>
            </li>
          ))}
        </ul>
      )}
      {overCap && (
        <p className="text-xs text-destructive">
          Total attached size is over the 2.5 MB limit ({mb(totalBytes)} MB) — remove a file.
        </p>
      )}
    </div>
  );
}
